"use client";

import React, { useState, useRef, useEffect } from "react";
import { Check, AlertCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

interface NewsletterSignupProps {
  variant: "inline" | "card";
  headline: string;
  description: string;
  buttonText?: string;          // default: "Subscribe"
  formId: string;               // Kit form ID
  successMessage?: string;      // default: "You're in! Check your inbox."
  alreadySubMessage?: string;   // default: "You're already subscribed!"
  tag?: string;                 // Kit tag name to apply
  className?: string;           // additional wrapper classes
}

type Status = "idle" | "submitting" | "success" | "already-subscribed" | "error";

export default function NewsletterSignup({
  variant,
  headline,
  description,
  buttonText = "Subscribe",
  formId,
  successMessage = "You're in! Check your inbox to confirm your subscription.",
  alreadySubMessage = "You're already subscribed! Look out for our next email.",
  tag,
  className,
}: NewsletterSignupProps) {
  const [email, setEmail] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [errorMessage, setErrorMessage] = useState("");
  
  const successHeadingRef = useRef<HTMLHeadingElement>(null);
  const headlineId = `newsletter-heading-${variant}`;
  const errorId = `newsletter-error-${variant}`;
  const helperId = `newsletter-helper-${variant}`;

  // Feature flag check
  if (process.env.NEXT_PUBLIC_NEWSLETTER_ENABLED !== "true") {
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Reset state
    setStatus("submitting");
    setErrorMessage("");

    try {
      const response = await fetch("/api/newsletter", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          formId,
          tag,
          honeypot,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Something went wrong.");
      }

      if (data.alreadySubscribed) {
        setStatus("already-subscribed");
      } else {
        setStatus("success");
      }
    } catch (err: any) {
      setErrorMessage(err.message);
      setStatus("error");
    }
  };

  // Focus management: move focus to success heading when status changes
  useEffect(() => {
    if ((status === "success" || status === "already-subscribed") && successHeadingRef.current) {
      successHeadingRef.current.focus();
    }
  }, [status]);

  if (status === "success" || status === "already-subscribed") {
    return (
      <div 
        role="status" 
        aria-live="polite"
        className={cn(
          "rounded-3xl p-10 text-center animate-in fade-in duration-500",
          variant === "card" ? "bg-bloom-linen border border-bloom-border shadow-sm max-w-lg mx-auto" : "bg-bloom-green-light/10",
          className
        )}


      >
        <div className="flex justify-center mb-6" aria-hidden="true">
          <div className="w-16 h-16 rounded-full bg-bloom-green-deep/10 flex items-center justify-center text-bloom-green-deep">
            <Check size={32} />
          </div>
        </div>
        <h2 
          ref={successHeadingRef}
          tabIndex={-1}
          className="font-serif text-2xl font-bold text-charcoal mb-4 outline-none"
        >
          {status === "success" ? "Welcome to the community!" : "Welcome back!"}
        </h2>
        <p className="font-sans text-muted-foreground max-w-sm mx-auto leading-relaxed">
          {status === "success" ? successMessage : alreadySubMessage}
        </p>

      </div>
    );
  }



  return (
    <section 
      aria-labelledby={headlineId}
      className={cn(
        "rounded-[40px] overflow-hidden transition-all duration-300",
        variant === "card" 
          ? "bg-white border border-bloom-border p-8 md:p-12 shadow-[0_8px_32px_-8px_rgba(27,67,50,0.10)] max-w-xl mx-auto text-center" 
          : "bg-white border border-bloom-border p-8 md:p-14 lg:p-20 shadow-sm",
        className
      )}


    >
      <form 
        role="form" 
        onSubmit={handleSubmit}
        className={cn(
          "grid gap-8 items-center",
          variant === "inline" ? "lg:grid-cols-2" : "grid-cols-1"
        )}
      >
        <div>

          <h2 id={headlineId} className={cn(
            "font-serif font-black text-charcoal leading-tight mb-4",
            variant === "card" ? "text-2xl md:text-3xl" : "text-2xl md:text-3xl lg:text-[32px]"
          )}>
            {headline}
          </h2>

          <p className={cn(
            "font-sans text-muted-foreground leading-relaxed",
            variant === "card" ? "text-base" : "text-sm"
          )}>
            {description}
          </p>

        </div>

        <div className="space-y-4">
          {/* Honeypot field - visually hidden */}
          <div className="absolute left-[-9999px] h-0 opacity-0 overflow-hidden" aria-hidden="true">
            <label htmlFor="website">Website</label>
            <input
              id="website"
              name="website"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label 
              htmlFor={`email-${variant}`}
              className="font-sans font-bold uppercase tracking-widest text-xs text-charcoal/60 block text-left ml-1"
            >
              Email address
            </Label>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 relative">
                <Input
                  id={`email-${variant}`}
                  type="email"
                  placeholder="name@example.com"
                  required
                  aria-required="true"
                  aria-invalid={status === "error"}
                  aria-describedby={status === "error" ? errorId : helperId}
                  disabled={status === "submitting"}
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (status === "error") setStatus("idle");
                  }}
                  className={cn(
                    "h-12 bg-background border-input focus:ring-bloom-green-deep focus:border-bloom-green-deep transition-all",
                    status === "error" && "border-destructive focus:ring-destructive focus:border-destructive"
                  )}
                />
              </div>
              <Button
                type="submit"
                disabled={status === "submitting"}
                aria-busy={status === "submitting"}
                className="h-14 px-10 bg-bloom-green-deep hover:bg-bloom-green-mid text-bloom-cream font-sans font-bold uppercase tracking-widest text-[13px] rounded-xl transition-all active:scale-95 shadow-lg"
              >


                {status === "submitting" ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Subscribing
                  </>
                ) : buttonText}
              </Button>
            </div>
          </div>

          {/* Messages info/error */}
          <div className="min-h-[20px]">
            {status === "error" && (
              <p id={errorId} role="alert" className="flex items-center gap-2 text-destructive font-sans text-xs font-medium">
                <AlertCircle size={14} aria-hidden="true" />
                {errorMessage}
              </p>
            )}
            <p id={helperId} className="font-sans text-xs text-muted-foreground/70 italic text-left ml-1">
              Unsubscribe at any time. We value your privacy.
            </p>

          </div>
        </div>
      </form>
    </section>
  );
}
